/**
 * Deterministic JSON: object keys sorted recursively so the same state always produces the same string
 * (stable checksums, diff-friendly exports). Rejects NaN/Infinity instead of silently writing null.
 */
export function canonicalStringify(value: unknown): string {
  return JSON.stringify(sortDeep(value, '$'));
}

function sortDeep(value: unknown, path: string): unknown {
  if (typeof value === 'number' && !Number.isFinite(value)) {
    throw new TypeError(`Non-finite number at ${path}`);
  }
  if (Array.isArray(value)) return value.map((v, i) => sortDeep(v, `${path}[${i}]`));
  if (value !== null && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) {
      const v = (value as Record<string, unknown>)[key];
      if (v !== undefined) out[key] = sortDeep(v, `${path}.${key}`);
    }
    return out;
  }
  return value;
}

/** FNV-1a 32-bit over UTF-16 code units, hex. Detects corruption; it is not a security measure. */
export function fnv1a32(text: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}
